import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import AdminAfasTransactionInnerDetails from './AdminAfasTransactionInnerDetails';
import { authHeaders } from '../utils/apiAuth';
import { errorText } from '../utils/apiErrors';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';
import CreateOrderModal from './CreateOrderModal';

const SortableLinesTable = ({ lines, title, icon, language, t }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'Amount', direction: 'desc' });

  const sortedLines = useMemo(() => {
    let sortableLines = [...lines];
    if (sortConfig.key !== null) {
      sortableLines.sort((a, b) => {
        if (sortConfig.key === 'period') {
          const periodA = (parseInt(a.Year) || 0) * 100 + (parseInt(a.Month) || 0);
          const periodB = (parseInt(b.Year) || 0) * 100 + (parseInt(b.Month) || 0);
          return sortConfig.direction === 'asc' ? periodA - periodB : periodB - periodA;
        }

        const getVal = (obj, key) => {
          let val = obj[key];
          if (key === 'Amount' || key === 'CostCenterNr') {
            return parseFloat(val) || 0;
          }
          return String(val || '').toLowerCase();
        };

        const compare = (valA, valB, dir) => {
          if (valA < valB) return dir === 'asc' ? -1 : 1;
          if (valA > valB) return dir === 'asc' ? 1 : -1;
          return 0;
        };

        return compare(getVal(a, sortConfig.key), getVal(b, sortConfig.key), sortConfig.direction);
      });
    }
    return sortableLines;
  }, [lines, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return (
      <svg className="w-3 h-3 ml-1 opacity-20 group-hover:opacity-50 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
    );
    return sortConfig.direction === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-primary animate-bounce-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" /></svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-primary animate-bounce-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
    );
  };

  return (
    <div className="mb-6 last:mb-0">
      <h4 className="font-semibold text-gray-700 mb-2 uppercase text-[10px] tracking-widest flex items-center">
        {icon}
        {title}
      </h4>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-purple-100 uppercase text-primary text-[10px] font-bold tracking-wider">
            <tr>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group" onClick={() => requestSort('AccountType')}>
                <div className="flex items-center">{t?.account || 'Account'} {getSortIcon('AccountType')}</div>
              </th>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group" onClick={() => requestSort('LineDescription')}>
                <div className="flex items-center">{t?.description || 'Description'} {getSortIcon('LineDescription')}</div>
              </th>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group text-center" onClick={() => requestSort('period')}>
                <div className="flex items-center justify-center">{t?.period || 'Period'} {getSortIcon('period')}</div>
              </th>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group" onClick={() => requestSort('CostCenterNr')}>
                <div className="flex items-center">{t?.cost_center || 'Cost Center'} {getSortIcon('CostCenterNr')}</div>
              </th>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group text-right" onClick={() => requestSort('Amount')}>
                <div className="flex items-center justify-end">{t?.amount || 'Amount'} {getSortIcon('Amount')}</div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedLines.map((line, idx) => {
              const desc = language === 'nl' ? line.AccountDescriptionNL : line.AccountDescriptionEN;
              return (
                <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                  <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">
                    {line.AccountType}
                    <span className="text-gray-400 font-normal ml-1">({line.AccountNr})</span>
                  </td>
                  <td className="px-4 py-3 text-gray-900 min-w-[200px]">
                    <div className="font-bold text-[13px] text-gray-800 leading-tight">{desc}</div>
                    <div className="text-[11px] text-gray-500 font-normal mt-1">{line.LineDescription}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700 font-bold bg-gray-50/20">
                    <span className="inline-flex items-center justify-center h-6 px-2 rounded bg-white border border-gray-200 shadow-xs tabular-nums text-[12px]">
                      {line.Month && line.Year ? `${String(line.Month).padStart(2, '0')} / ${line.Year}` : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="flex flex-wrap gap-1 items-center">
                      {line.CostCenterNr && (
                        <span className="inline-block bg-blue-50 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-blue-100">#{line.CostCenterNr}</span>
                      )}
                      {line.CostCenterName && (
                        <span className="text-[11px] text-gray-500 truncate max-w-[150px]" title={line.CostCenterName}>{line.CostCenterName}</span>
                      )}
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right font-bold tabular-nums whitespace-nowrap ${line.Amount < 0 ? 'text-red-500 font-extrabold' : 'text-green-600'}`}>
                    € {parseFloat(line.Amount).toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Journal2GridView = ({ lines, language, t }) => {
  const summarized = useMemo(() => {
    const groups = {};
    const minPeriodValue = lines.length > 0
      ? Math.min(...lines.map(l => {
        const pv = (parseInt(l.Year) || 0) * 100 + (parseInt(l.Month) || 0);
        return pv > 0 ? pv : Infinity;
      }))
      : Infinity;

    lines.forEach(line => {
      const period = line.Month && line.Year ? `${String(line.Month).padStart(2, '0')} / ${line.Year}` : (t?.unknown || 'Unknown');
      const periodValue = (parseInt(line.Year) || 0) * 100 + (parseInt(line.Month) || 0);

      if (!groups[period]) {
        groups[period] = {
          period,
          periodValue,
          totalAmount: 0,
          items: []
        };
      }

      const desc = language === 'nl' ? line.AccountDescriptionNL : line.AccountDescriptionEN;
      const amount = parseFloat(line.Amount) || 0;

      // Group by description within each period, EXCEPT for the first month
      const isFirstMonth = periodValue === minPeriodValue;
      const existingItem = isFirstMonth ? null : groups[period].items.find(i => i.description === desc);

      if (existingItem) {
        existingItem.amount += amount;
        existingItem.count = (existingItem.count || 1) + 1;
        // Update description to indicate grouping
        existingItem.lineDescription = t?.multiple_lines_summed || (language === 'nl'
          ? "Meerdere transacties gesummeerd"
          : "Multiple transactions summed");
      } else {
        groups[period].items.push({
          description: desc,
          lineDescription: line.LineDescription,
          amount: amount,
          count: 1
        });
      }

      groups[period].totalAmount += amount;
    });

    const result = Object.values(groups).sort((a, b) => a.periodValue - b.periodValue);

    // Sort items within each period: negative amounts at the bottom
    result.forEach(group => {
      group.items.sort((a, b) => b.amount - a.amount);
    });

    return result;
  }, [lines, language, t]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6">
      {summarized.map((item, idx) => (
        <div key={idx} className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col hover:border-primary/40 hover:shadow-md transition-all group overflow-hidden">
          <div className="flex justify-between items-start mb-3">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 group-hover:text-primary transition-colors">{t?.period || 'Period'}</span>
              <span className="text-sm font-black text-gray-800 tracking-tight">{item.period}</span>
            </div>
            <div className="text-right flex flex-col items-end pt-1">
              <span className={`text-md font-black tabular-nums ${item.totalAmount < 0 ? 'text-red-500' : 'text-green-600'}`}>
                € {item.totalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="space-y-3 mt-1 pt-3 border-t border-gray-100/60">
            {item.items.map((entry, eIdx) => (
              <div key={eIdx} className="flex justify-between items-start gap-3 group/entry">
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-bold text-gray-700 leading-tight group-hover/entry:text-primary transition-colors truncate" title={entry.description}>
                    {entry.description}
                  </div>
                  <div className="text-[10px] text-gray-400 font-medium truncate mt-0.5" title={entry.lineDescription}>
                    {entry.lineDescription}
                  </div>
                </div>
                <div className={`text-[11px] font-bold tabular-nums whitespace-nowrap mt-0.5 ${entry.amount < 0 ? 'text-red-400' : 'text-green-500'}`}>
                  {entry.amount < 0 ? '-' : '+'}€{Math.abs(entry.amount).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-auto pt-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="h-1 w-full bg-gradient-to-r from-primary/20 to-transparent rounded-full"></div>
          </div>
        </div>
      ))}
    </div>
  );
};


const PdfDownloadButton = ({ voucherNr, apiUrl, t, onPdfChecked }) => {
  const [pdfInfo, setPdfInfo] = useState({ checking: true, exists: false, guid: null });

  useEffect(() => {
    const checkPdf = async () => {
      try {
        const res = await fetch(`${apiUrl}/afas/check-invoice-pdf/${voucherNr}`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setPdfInfo({ checking: false, exists: data.exists, guid: data.guid });
          if (onPdfChecked) onPdfChecked(data.exists);
        } else {
          setPdfInfo({ checking: false, exists: false, guid: null });
          if (onPdfChecked) onPdfChecked(false);
        }
      } catch (err) {
        setPdfInfo({ checking: false, exists: false, guid: null });
        if (onPdfChecked) onPdfChecked(false);
      }
    };
    checkPdf();
  }, [voucherNr, apiUrl, onPdfChecked]);

  if (pdfInfo.checking || !pdfInfo.exists) return null;

  const handleDownload = async () => {
    try {
      const res = await fetch(`${apiUrl}/afas/download-invoice-pdf/${voucherNr}?guid=${pdfInfo.guid}`, {
        headers: authHeaders()
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${voucherNr}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Failed to download PDF", await res.text());
        alert(t?.downloadFailed || 'Failed to download PDF');
      }
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert(t?.downloadError || 'Error downloading PDF');
    }
  };

  return (
    <button
      onClick={handleDownload}
      title="Download PDF Invoice from AFAS"
      className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-md transition-colors text-xs font-bold uppercase tracking-wider shadow-sm group"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      {t?.pdf || 'PDF'}
    </button>
  );
};

const matchesSearch = (trx, needle) =>
  [trx.VoucherNr, trx.customerDomain, trx.orderUser, trx.customerId, trx.orderType, trx.debtorId, trx.debtorName]
    .some(value => String(value || '').toLowerCase().includes(needle));

const matchesType = (trx, type) => {
  if (!type || type === 'ALL') return true;
  if (type === 'Credit Note') return Boolean(trx.isCreditNote);
  return (trx.orderType || '').split(', ').map(s => s.trim().toLowerCase()).includes(type.toLowerCase());
};

const AdminAfasTransactionsSkeleton = ({ t }) => {
  return (
    <div className="animate-pulse">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary text-white opacity-85">
              <tr>
                <th scope="col" className="px-6 py-3 w-10"></th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider w-48">{t?.voucher_nr || 'Voucher Nr'}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">{t?.last_synced_at || 'Last Synced'}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">{t?.status || 'Status'}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">{t?.domain || 'Domain'}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">{t?.type || 'Type'}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider w-36">{t?.afas_lines_count || 'Lines Count'}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">{t?.order_user || 'Order User'}</th>
                <th scope="col" className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((rowIdx) => (
                <tr key={rowIdx}>
                  <td className="px-6 py-4 text-gray-500 w-10">
                    <div className="h-5 w-5 bg-gray-200 rounded-full mx-auto"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-48"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-6 w-12 bg-gray-200 rounded-full"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                      <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminAfasTransactions = ({ t, language }) => {
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [ordersData, setOrdersData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [loadingOrders, setLoadingOrders] = useState({});
  const [journal2ViewModes, setJournal2ViewModes] = useState({}); // { [voucherNr]: 'table' | 'grid' }
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failedOffset, setFailedOffset] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const observer = useRef();
  const queryGeneration = useRef(0);
  const limit = 20;

  const apiUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchTransactions = useCallback(async (currentOffset = 0, isLoadMore = false) => {
    // A new search or date range drops responses still in flight for the previous one.
    const generation = isLoadMore ? queryGeneration.current : ++queryGeneration.current;
    const isCurrent = () => generation === queryGeneration.current;
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      let url = `${apiUrl}/afas/transactions`;
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (debouncedSearchQuery) params.append('q', debouncedSearchQuery);
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (typeFilter !== 'ALL') params.append('type', typeFilter);
      params.append('limit', limit);
      params.append('offset', currentOffset);
      const queryStr = params.toString();
      if (queryStr) {
        url += `?${queryStr}`;
      }

      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !Array.isArray(data)) {
        throw new Error(errorText(data, t, 'Failed to fetch AFAS transactions'));
      }
      if (!isCurrent()) return;

      if (data.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      if (isLoadMore) {
        setTransactions(prev => {
          const newItems = data.filter(d => !prev.some(p => p.VoucherNr === d.VoucherNr));
          return [...prev, ...newItems];
        });
      } else {
        setTransactions(data);
      }
    } catch (err) {
      console.error(err);
      if (!isCurrent()) return;
      if (isLoadMore) {
        setFailedOffset(currentOffset);
        toast.error(err.message);
      } else {
        setError(err.message);
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else if (isCurrent()) {
        setLoading(false);
      }
    }
  }, [apiUrl, startDate, endDate, debouncedSearchQuery, statusFilter, typeFilter, t]);

  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    setFailedOffset(null);
    fetchTransactions(0, false);
  }, [fetchTransactions]);

  const retryLoadMore = () => {
    setFailedOffset(null);
    fetchTransactions(failedOffset, true);
  };

  const typedSearch = searchQuery.trim().toLowerCase();
  // Until the server answers for what was typed, the loaded rows are narrowed locally.
  const searchPending = typedSearch !== debouncedSearchQuery || (loading && !!typedSearch);

  const lastTransactionElementRef = useCallback(node => {
    if (loading || loadingMore || searchPending || failedOffset !== null) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setOffset(prev => {
          const nextOffset = prev + limit;
          fetchTransactions(nextOffset, true);
          return nextOffset;
        });
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, searchPending, failedOffset, hasMore, fetchTransactions]);

  const toggleRow = async (voucherNr) => {
    const isCurrentlyExpanded = !!expandedRows[voucherNr];

    setExpandedRows((prev) => ({
      ...prev,
      [voucherNr]: !isCurrentlyExpanded,
    }));

    if (!isCurrentlyExpanded && !ordersData[voucherNr]) {
      try {
        setLoadingOrders(prev => ({ ...prev, [voucherNr]: true }));
        const res = await fetch(`${apiUrl}/api/orders/${voucherNr}`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setOrdersData(prev => ({ ...prev, [voucherNr]: data }));
        } else {
          setOrdersData(prev => ({ ...prev, [voucherNr]: null }));
        }
      } catch (err) {
        console.error("Failed to fetch order", err);
      } finally {
        setLoadingOrders(prev => ({ ...prev, [voucherNr]: false }));
      }
    }
  };

  const handleDeleteTransaction = async (e, voucherNr) => {
    e.stopPropagation();
    if (!window.confirm(t?.confirm_delete_all_collections || 'This will delete data from orders, afas_transactions, and transactions collections. Are you really sure?\n\n(Dit verwijdert gegevens uit de orders, afas_transactions en transactions collecties. Weet u het heel zeker?)')) {
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/afas/transactions/${voucherNr}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (res.ok) {
        toast.success(t?.delete_success || 'Transaction deleted successfully.');
        setTransactions(prev => prev.filter(trx => trx.VoucherNr !== voucherNr));
      } else {
        const errorData = await res.json();
        toast.error(`Failed to delete: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(err);
      toast.error(t?.error_deleting || 'Error deleting transaction');
    }
  };

  const visibleTransactions = (searchPending
    ? transactions.filter(trx => matchesSearch(trx, typedSearch))
    : transactions).filter(trx => matchesType(trx, typeFilter));

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const loadingToast = toast.loading('Generating CSV export...');
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const queryStr = params.toString();
      const url = `${apiUrl}/api/admin/orders/export-csv${queryStr ? `?${queryStr}` : ''}`;
      
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `orders_export_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(loadingToast);
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error(err);
      toast.dismiss();
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const typeFilterOptions = [
    {
      label: t?.all_types || 'All Types',
      value: 'ALL',
      activeClass: 'bg-primary text-white border-primary shadow-sm shadow-primary/20',
      inactiveClass: 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200'
    },
    {
      label: 'GCP',
      value: 'GCP',
      activeClass: 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-600/20',
      inactiveClass: 'bg-white hover:bg-purple-50/50 text-purple-700 border-purple-200'
    },
    {
      label: t?.renewal || 'Renewal',
      value: 'Renewal',
      activeClass: 'bg-sky-600 text-white border-sky-600 shadow-sm shadow-sky-600/20',
      inactiveClass: 'bg-white hover:bg-sky-50/50 text-sky-700 border-sky-200'
    },
    {
      label: t?.annual || 'Annual',
      value: 'Annual',
      activeClass: 'bg-emerald-600 text-white border-emerald-600 shadow-sm shadow-emerald-600/20',
      inactiveClass: 'bg-white hover:bg-emerald-50/50 text-emerald-700 border-emerald-200'
    },
    {
      label: 'Flex',
      value: 'Flex',
      activeClass: 'bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-500/20',
      inactiveClass: 'bg-white hover:bg-amber-50/50 text-amber-700 border-amber-200'
    },
    {
      label: t?.credit_note || 'Credit Note',
      value: 'Credit Note',
      activeClass: 'bg-rose-600 text-white border-rose-600 shadow-sm shadow-rose-600/20',
      inactiveClass: 'bg-white hover:bg-rose-50/50 text-rose-700 border-rose-200'
    }
  ];

  return (
    <div className="p-4 sm:p-8 space-y-8">
      <ReadOnlyNotice t={t} />
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t.admin_afas_transactions || 'AFAS Transactions'}
          </h2>
          <p className="text-gray-600 mt-2">
            {t.admin_afas_transactions_description || 'Overview of synced AFAS transactions.'}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full xl:w-auto mt-4 xl:mt-0">
          {canWrite && (
            <button
              type="button"
              onClick={() => setIsCreateOrderOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-lg bg-primary text-white hover:opacity-90 shadow-sm transition-all shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t.create_order || 'Create Order'}
            </button>
          )}
          <button
            onClick={handleExportCSV}
            disabled={isExporting}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-lg border transition-colors ${
              isExporting ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-primary border-primary hover:bg-purple-50'
            }`}
            title="Export Orders CSV"
          >
            {isExporting ? (
              <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            export CSV
          </button>
          
          {/* Date Filters */}
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 bg-white w-full sm:w-auto overflow-hidden">
            <svg className="h-5 w-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
              title="Start Date"
            />
            <span className="text-gray-300">-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
              title="End Date"
            />
          </div>

          <div className="relative w-full sm:w-64 xl:w-80 shrink-0">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm transition-all"
              placeholder={t?.search || 'Search by voucher, domain, user, type...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        {/* Status filters on the left */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setStatusFilter('ALL')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${statusFilter === 'ALL' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t?.filter_all || 'All'}
          </button>
          <button
            onClick={() => setStatusFilter('SYNCED')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${statusFilter === 'SYNCED' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t?.synced || 'Synced'}
          </button>
          <button
            onClick={() => setStatusFilter('MISSING')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${statusFilter === 'MISSING' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t?.missing || 'Missing'}
          </button>
          <button
            onClick={() => setStatusFilter('SKIPPED')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${statusFilter === 'SKIPPED' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t?.skipped || 'Skipped'}
          </button>
        </div>

        {/* Quick Type Selection Tray at the end of the row */}
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider select-none shrink-0 mr-1">
            {t?.type || 'Type'}:
          </span>
          {typeFilterOptions.map((opt) => {
            const isActive = typeFilter === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setTypeFilter(opt.value)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition duration-200 shrink-0 select-none ${
                  isActive ? opt.activeClass : opt.inactiveClass
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {error ? (
        <div className="p-4 text-red-600">{t.error_title || 'Error'}: {error}</div>
      ) : (loading && !searchPending) || (searchPending && visibleTransactions.length === 0) ? (
        <AdminAfasTransactionsSkeleton t={t} />
      ) : visibleTransactions.length === 0 ? (
        <div className="text-gray-500 bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
          {transactions.length === 0 && !debouncedSearchQuery && statusFilter === 'ALL' && typeFilter === 'ALL' ? (t.no_data_found || 'No data found.') : (t?.no_search_results || 'No transactions matched your search.')}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary">
                <tr>
                  <th scope="col" className="px-6 py-3 border-b border-primary"></th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t.voucher_nr || 'Voucher Nr'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t.last_synced_at || 'Last Synced'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t.status || 'Status'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t.domain || 'Domain'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t.type || 'Type'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t.afas_lines_count || 'Lines Count'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t.order_user || 'Order User'}</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-white uppercase tracking-wider border-b border-primary"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleTransactions.map((trx, index) => {
                  const isExpanded = !!expandedRows[trx.VoucherNr];
                  const lines = trx.lines || [];
                  // eslint-disable-next-line no-unused-vars
                  const journal1Lines = lines.filter(l => l.BookTypeCode === "40");
                  // eslint-disable-next-line no-unused-vars
                  const journal2Lines = lines.filter(l => l.BookTypeCode === "88");
                  // eslint-disable-next-line no-unused-vars
                  const otherLines = lines.filter(l => l.BookTypeCode !== "40" && l.BookTypeCode !== "88");
                  const rowKey = trx.VoucherNr || index;

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        ref={index === visibleTransactions.length - 1 ? lastTransactionElementRef : null}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleRow(trx.VoucherNr)}
                      >
                        <td className="px-6 py-4 text-gray-500 w-10">
                          {isExpanded ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </td>
                        <td className="px-6 py-4 font-semibold text-primary">{trx.VoucherNr}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {trx.lastSyncedAt ? new Date(trx.lastSyncedAt).toLocaleString(language === 'nl' ? 'nl-NL' : 'en-US') : (trx.createdAt ? new Date(trx.createdAt).toLocaleString(language === 'nl' ? 'nl-NL' : 'en-US') : '-')}
                        </td>
                        <td className="px-6 py-4">
                          {trx.missingAfas ? (
                            trx.afasSkipped ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-gray-50 text-gray-700 border-gray-200">
                                {t?.skipped || 'Skipped'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-red-50 text-red-700 border-red-200">
                                {t?.missing || 'Missing'}
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-green-50 text-green-700 border-green-200">
                              {t?.synced || 'Synced'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 truncate">
                            {trx.customerId && trx.customerId !== '0' ? (
                              <Link
                                to={`/admin/customers/${trx.customerId}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm font-bold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer transition-colors truncate shrink-0 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100"
                              >
                                {trx.customerId}
                              </Link>
                            ) : (trx.debtorId ? (
                              <Link
                                to={`/admin/customers/${trx.debtorId}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm font-bold text-purple-700 hover:text-purple-900 hover:underline cursor-pointer transition-colors truncate shrink-0 bg-purple-50 hover:bg-purple-100 px-1.5 py-0.5 rounded border border-purple-200"
                                title={trx.debtorName || trx.debtorId}
                              >
                                {trx.debtorId}
                              </Link>
                            ) : null)}
                            {trx.customerDomain ? (
                              <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 truncate">
                                {trx.customerDomain}
                              </span>
                            ) : (trx.debtorName ? (
                              <span className="text-xs text-gray-600 truncate" title={trx.debtorName}>
                                {trx.debtorName}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-600">-</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {trx.orderType || trx.isCreditNote || trx.creditedBy ? (
                            <div className="flex flex-wrap gap-1">
                              {trx.orderType && trx.orderType.split(', ').map((typeStr, i) => {
                                let colorClass = "bg-gray-50 text-gray-700 border-gray-200";
                                if (typeStr === "Annual") colorClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
                                else if (typeStr === "Renewal") colorClass = "bg-sky-50 text-sky-700 border-sky-200";
                                else if (typeStr === "Flex") colorClass = "bg-amber-50 text-amber-700 border-amber-200";
                                else if (typeStr === "GCP") colorClass = "bg-purple-50 text-purple-700 border-purple-200";

                                return (
                                  <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${colorClass}`}>
                                    {typeStr}
                                  </span>
                                );
                              })}
                              {trx.isCreditNote && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-red-50 text-red-700 border-red-200">
                                  CREDIT NOTE
                                </span>
                              )}
                              {trx.creditedBy && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-gray-100 text-gray-500 border-gray-300">
                                  CREDITED
                                </span>
                              )}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4">
                          {trx.missingAfas ? (
                            <span className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded-full">
                              0
                            </span>
                          ) : (
                            <span className="bg-gray-200 text-gray-800 text-xs font-bold px-2 py-1 rounded-full">
                              {lines.length}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                          {trx.orderUser ? (
                            <div className="flex items-center gap-2">
                              {trx.orderUserPicture ? (
                                <img
                                  src={trx.orderUserPicture}
                                  alt={trx.orderUser}
                                  className="w-6 h-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                  referrerPolicy="no-referrer"
                                />
                              ) : trx.orderUser === 'System (Auto-Renewal)' ? (
                                <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-primary flex-shrink-0">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xs flex-shrink-0">
                                  {trx.orderUser.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <span className="truncate max-w-[200px]" title={trx.orderUser}>{trx.orderUser}</span>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          {canWrite && <button
                            onClick={(e) => handleDeleteTransaction(e, trx.VoucherNr)}
                            className="inline-flex items-center justify-center p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200 mr-2"
                            title={t?.delete || 'Delete'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>}
                          {trx.missingAfas ? (
                            <button
                              disabled
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center justify-center p-2 text-gray-300 cursor-not-allowed rounded-lg border border-transparent"
                              title="AFAS data is missing"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </button>
                          ) : (
                            <Link
                              to={`/admin/afas-transactions/${trx.VoucherNr}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center justify-center p-2 text-gray-500 hover:text-primary hover:bg-purple-50 rounded-lg transition-colors border border-transparent hover:border-purple-200"
                              title={t.view_details || 'View Details'}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </Link>
                          )}
                        </td>
                      </tr>

                      {isExpanded && ordersData[trx.VoucherNr] !== undefined && (
                        <tr className="bg-gray-50">
                          <td colSpan="9" className="px-6 py-6 border-b bg-gray-50/50">
                            <AdminAfasTransactionInnerDetails
                              trx={trx}
                              orderData={ordersData[trx.VoucherNr]}
                              journal2ViewMode={journal2ViewModes[trx.VoucherNr]}
                              setJournal2ViewMode={(mode) => setJournal2ViewModes(prev => ({ ...prev, [trx.VoucherNr]: mode }))}
                              apiUrl={apiUrl}
                              language={language}
                              t={t}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {failedOffset !== null && !loadingMore && (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-sm text-gray-600">
                      {t?.load_more_failed || "Couldn't load more transactions."}{' '}
                      <button type="button" onClick={retryLoadMore} className="font-semibold text-primary hover:underline">
                        {t?.retry || 'Retry'}
                      </button>
                    </td>
                  </tr>
                )}
                {(loadingMore || searchPending) && (
                  [1, 2, 3].map((rowIdx) => (
                    <tr key={`loading-more-${rowIdx}`} className="animate-pulse bg-white border-b border-gray-100">
                      <td className="px-6 py-4 w-10">
                        <div className="h-5 w-5 bg-gray-200 rounded-full mx-auto"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-28"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-48"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-6 w-12 bg-gray-200 rounded-full"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-6 py-4 flex justify-end gap-2">
                        <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canWrite && (
        <CreateOrderModal
          isOpen={isCreateOrderOpen}
          onClose={() => setIsCreateOrderOpen(false)}
          onSuccess={() => fetchTransactions(0, false)}
          t={t}
        />
      )}
    </div>
  );
};

export default AdminAfasTransactions;
export { SortableLinesTable, Journal2GridView, PdfDownloadButton };
